// Open and Save As pickers (File System Access API, Chromium on desktop).
//
// The picker ids make the browser remember the last folder used: one for files
// coming in (Open Files, Open Folder, Replace) and one for files going out
// (Download image, Export, Save). Browsers without the pickers keep the plain
// <input type="file"> and ordinary downloads; so do phones for opening (below).

const IN = "abvme-in";
const OUT = "abvme-out";

const MIME = { ".png": "image/png", ".jpg": "image/jpeg", ".jpeg": "image/jpeg", ".txt": "text/plain", ".json": "application/json", ".zip": "application/zip" };

const isAbort = (err) => err?.name === "AbortError";

// Phones get the plain file input: Android's open picker is the system one only,
// while the input also offers other file manager apps (e.g. MiXplorer).
const touchOnly = () => matchMedia("(pointer: coarse)").matches && !matchMedia("(any-pointer: fine)").matches;

export const canPickOpen = () => typeof window.showOpenFilePicker === "function" && !touchOnly();
export const canPickFolder = () => typeof window.showDirectoryPicker === "function" && !touchOnly();
export const canSaveAs = () => typeof window.showSaveFilePicker === "function";

/**
 * Ask which files to open. Returns File[] ([] when cancelled), or null when the
 * picker cannot be used here (the caller falls back to its file input).
 * accept: {description, extensions} to filter the list (All Files stays available).
 */
export async function pickFiles({ multiple = false, accept = null } = {}) {
  if (!canPickOpen()) return null;
  const options = { id: IN, multiple };
  if (accept) options.types = [{ description: accept.description, accept: { [accept.mime || "application/octet-stream"]: accept.extensions } }];
  // only: no "All files" choice in the picker (e.g. a texture takes images only).
  if (accept?.only) options.excludeAcceptAllOption = true;
  try {
    const handles = await window.showOpenFilePicker(options);
    return await Promise.all(handles.map(fileWithHandle));
  } catch (err) {
    return isAbort(err) ? [] : null;
  }
}

/** Ask for a folder and return the files directly inside it ([] when cancelled, null when unavailable). */
export async function pickFolderFiles() {
  if (!canPickFolder()) return null;
  let dir;
  try {
    dir = await window.showDirectoryPicker({ id: IN, mode: "read" });
  } catch (err) {
    return isAbort(err) ? [] : null;
  }
  const files = [];
  for await (const handle of dir.values()) {
    if (handle.kind !== "file") continue;
    const file = await fileWithHandle(handle);
    file.folder = dir;
    files.push(file);
  }
  return files;
}

/** The File, carrying its handle (`file.handle`) so it can be read again after a reload. */
async function fileWithHandle(handle) {
  const file = await handle.getFile();
  file.handle = handle;
  return file;
}

/**
 * Read a file again from a kept handle. Returns the File, "prompt" when the
 * browser needs the user's permission first (ask with requestHandleAccess from
 * a click), or null when the file cannot be read (moved, deleted, no support).
 */
export async function fileFromHandle(handle, { request = false } = {}) {
  if (!handle?.getFile) return null;
  try {
    let state = (await handle.queryPermission?.({ mode: "read" })) ?? "granted";
    if (state === "prompt" && request) state = await handle.requestPermission({ mode: "read" });
    if (state === "prompt") return "prompt";
    if (state !== "granted") return null;
    return await fileWithHandle(handle);
  } catch {
    return null;
  }
}

/**
 * Read the files of a File > Open Recent entry again. Asks for permission when
 * the browser needs it, so call this from a click (an entry with stored copies
 * needs none). Each File gets `recentId`, so opening it does not list it twice.
 * Returns {files, missing}:
 * the Files that could be read, and the names of those that could not (moved,
 * deleted, or access was refused).
 */
export async function filesFromRecent(entry) {
  const files = [];
  const missing = [];
  let dir = null;
  if (entry.folder) {
    try {
      let state = (await entry.folder.queryPermission({ mode: "read" })) ?? "granted";
      if (state === "prompt") state = await entry.folder.requestPermission({ mode: "read" });
      if (state === "granted") dir = entry.folder;
    } catch {
      dir = null;
    }
  }
  for (const { name, handle, blob } of entry.files) {
    let file = null;
    if (blob) {
      file = blob instanceof File ? blob : new File([blob], name, { type: blob.type });
    } else if (dir) {
      try {
        file = await fileWithHandle(await dir.getFileHandle(name));
        file.folder = dir;
      } catch {
        file = null;
      }
    } else if (!entry.folder) {
      file = await fileFromHandle(handle, { request: true });
    }
    if (file && file !== "prompt") {
      file.recentId = entry.id;
      files.push(file);
    } else missing.push(name);
  }
  return { files, missing };
}

function saveTypes(name) {
  const m = /\.[A-Za-z0-9]{1,15}$/.exec(name);
  if (!m) return null;
  const ext = m[0].toLowerCase();
  return [{ description: `${ext.slice(1).toUpperCase()} file`, accept: { [MIME[ext] || "application/octet-stream"]: [ext] } }];
}

/**
 * Ask where to save a file. Returns a file handle, null when cancelled, or
 * undefined when the picker cannot be used (no API, or no user gesture left):
 * the caller then downloads the ordinary way.
 */
export async function pickSaveTarget(suggestedName) {
  if (!canSaveAs()) return undefined;
  const options = { id: OUT, startIn: "downloads", suggestedName };
  const types = saveTypes(suggestedName);
  try {
    return await window.showSaveFilePicker(types ? { ...options, types } : options);
  } catch (err) {
    if (isAbort(err)) return null;
    if (err?.name === "TypeError" && types) {
      // A file type the browser does not accept: ask without the filter.
      try {
        return await window.showSaveFilePicker(options);
      } catch (err2) {
        return isAbort(err2) ? null : undefined;
      }
    }
    return undefined;
  }
}

/** Write a Blob or a fetch Response body to a picked file. */
export async function writeFile(handle, data) {
  const writable = await handle.createWritable();
  try {
    if (data instanceof Blob) {
      await writable.write(data);
      await writable.close();
    } else {
      await data.body.pipeTo(writable);
    }
  } catch (err) {
    await writable.abort().catch(() => {});
    throw err;
  }
}

/**
 * The browser creates the picked file straight away. When nothing could be
 * written to it, remove it again, but only if it is still empty.
 */
export async function discardTarget(handle) {
  try {
    if ((await handle.getFile()).size === 0) await handle.remove?.();
  } catch {
    // Leave it: the browser may not support remove().
  }
}
